import React, { useCallback, useEffect, useRef, useState } from "react";
import { appReleasesApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Textarea, Btn } from "../shared/UI";

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (n <= 0) return "—";
  const mb = n / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

// Admin view: upload the latest APK build and share its public URL.
export default function AppReleaseView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [version, setVersion] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null); // { type: "ok"|"err", text }
  const [copiedId, setCopiedId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadReleases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await appReleasesApi.list();
      setRows(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load releases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReleases(); }, [loadReleases]);

  const acceptFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      setMessage({ type: "err", text: "Only .apk files are allowed." });
      return;
    }
    setFile(f);
    setMessage(null);
  };

  const onPickFile = (e) => {
    acceptFile(e.target.files?.[0] || null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    acceptFile(e.dataTransfer.files?.[0] || null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const resetForm = () => {
    setVersion("");
    setVersionCode("");
    setNotes("");
    setFile(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    setMessage(null);
    if (!version.trim()) {
      setMessage({ type: "err", text: "Please enter a version (e.g. 1.4.2)." });
      return;
    }
    if (!file) {
      setMessage({ type: "err", text: "Please choose an .apk file." });
      return;
    }
    if (!file.name.toLowerCase().endsWith(".apk")) {
      setMessage({ type: "err", text: "Only .apk files are allowed." });
      return;
    }

    const form = new FormData();
    form.append("apk", file);
    form.append("version", version.trim());
    if (versionCode.trim()) form.append("versionCode", versionCode.trim());
    if (notes.trim()) form.append("notes", notes.trim());

    setUploading(true);
    setProgress(0);
    try {
      const res = await appReleasesApi.upload(form, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      if (res.data?.status) {
        setMessage({ type: "ok", text: "APK uploaded successfully. Share URL is ready below." });
        resetForm();
        loadReleases();
      } else {
        setMessage({ type: "err", text: res.data?.message || "Upload failed." });
      }
    } catch (e) {
      setMessage({ type: "err", text: e.response?.data?.message || e.message || "Upload failed." });
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      // Fallback: select via prompt if clipboard is unavailable
      window.prompt("Copy this URL:", url);
    }
  };

  const latest = rows[0];

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>APP RELEASE — UPLOAD APK</SectionLabel>
        <div style={{ marginLeft: "auto" }}>
          <Btn variant="ghost" onClick={loadReleases} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Btn>
        </div>
      </div>

      <div style={styles.hint}>
        Upload the latest Android build. After upload you get a public share URL that can be sent to agents to install the app.
      </div>

      {/* Upload form */}
      <div style={styles.card}>
        <div style={styles.formGrid}>
          <Field label="VERSION *">
            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 1.4.2" />
          </Field>
          <Field label="VERSION CODE">
            <Input type="number" value={versionCode} onChange={(e) => setVersionCode(e.target.value)} placeholder="e.g. 42" />
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <Field label="RELEASE NOTES">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed in this build (optional)" rows={2} />
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={styles.fileLabel}>APK FILE *</div>
          <div
            style={{ ...styles.dropzone, ...(dragOver ? styles.dropzoneActive : {}), ...(uploading ? styles.dropzoneDisabled : {}) }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={onPickFile}
              style={{ display: "none" }}
            />
            <div style={styles.dropIcon}>{dragOver ? "📥" : "⬆️"}</div>
            {file ? (
              <>
                <div style={styles.dropFileName}>{file.name}</div>
                <div style={styles.fileMeta}>{formatBytes(file.size)} · click or drop to replace</div>
              </>
            ) : (
              <>
                <div style={styles.dropTitle}>
                  {dragOver ? "Drop the APK here" : "Drag & drop your APK here"}
                </div>
                <div style={styles.dropSub}>or click to browse · .apk only</div>
              </>
            )}
          </div>
        </div>

        {uploading && (
          <div style={styles.progressWrap}>
            <div style={{ ...styles.progressBar, width: `${progress}%` }} />
            <span style={styles.progressText}>{progress}%</span>
          </div>
        )}

        {message && (
          <div style={{ ...styles.message, color: message.type === "ok" ? T.acc : T.sos }}>
            {message.text}
          </div>
        )}

        <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
          <Btn onClick={handleUpload} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload APK"}
          </Btn>
          <Btn variant="ghost" onClick={resetForm} disabled={uploading}>Clear</Btn>
        </div>
      </div>

      {/* Latest release highlight */}
      {latest && (
        <div style={styles.latestCard}>
          <div style={styles.latestHead}>
            <span style={styles.latestTag}>LATEST</span>
            <span style={styles.latestVersion}>v{latest.version}{latest.versionCode != null ? ` (${latest.versionCode})` : ""}</span>
            <span style={styles.latestMeta}>{formatBytes(latest.sizeBytes)} · {formatDate(latest.createdAt)}</span>
          </div>
          <div style={styles.urlRow}>
            <input readOnly value={latest.url} style={styles.urlInput} onFocus={(e) => e.target.select()} />
            <Btn size="sm" onClick={() => copyUrl(latest.id, latest.url)}>
              {copiedId === latest.id ? "Copied!" : "Copy URL"}
            </Btn>
            <a href={latest.url} target="_blank" rel="noreferrer" style={styles.openBtn}>Open</a>
          </div>
        </div>
      )}

      {/* History */}
      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Version", "Code", "File", "Size", "Uploaded", "Share URL"].map((h) => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={styles.tr}>
                <td style={styles.td}><span style={{ fontWeight: 600 }}>v{r.version}</span></td>
                <td style={styles.tdM}>{r.versionCode ?? "—"}</td>
                <td style={styles.tdM} title={r.fileName}>{r.fileName}</td>
                <td style={styles.tdM}>{formatBytes(r.sizeBytes)}</td>
                <td style={styles.tdM}>{formatDate(r.createdAt)}</td>
                <td style={styles.td}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Btn size="sm" variant="outline" onClick={() => copyUrl(r.id, r.url)}>
                      {copiedId === r.id ? "Copied!" : "Copy"}
                    </Btn>
                    <a href={r.url} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td style={styles.empty} colSpan={6}>No APK uploaded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  wrap: { flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 },
  toolbar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  hint: { fontSize: 11, color: T.muted, marginTop: -6 },
  card: { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 12 },
  fileLabel: { fontFamily: T.fontMono, fontSize: 9, letterSpacing: "1.5px", color: T.muted, textTransform: "uppercase", marginBottom: 6 },
  fileMeta: { fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginTop: 4 },
  dropzone: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, textAlign: "center", padding: "26px 16px", borderRadius: 12, border: `2px dashed ${T.border2}`, background: T.s1, cursor: "pointer", transition: "all 0.18s" },
  dropzoneActive: { border: `2px dashed ${T.acc}`, background: "rgba(124,106,247,0.08)" },
  dropzoneDisabled: { opacity: 0.5, cursor: "not-allowed" },
  dropIcon: { fontSize: 26, lineHeight: 1 },
  dropTitle: { fontSize: 13, fontWeight: 600, color: T.text },
  dropSub: { fontSize: 11, color: T.muted },
  dropFileName: { fontSize: 13, fontWeight: 600, color: T.acc, wordBreak: "break-all" },
  progressWrap: { position: "relative", height: 18, background: T.s3, borderRadius: 9, marginTop: 14, overflow: "hidden", border: `1px solid ${T.border}` },
  progressBar: { position: "absolute", left: 0, top: 0, bottom: 0, background: T.acc, borderRadius: 9, transition: "width 0.2s" },
  progressText: { position: "absolute", right: 8, top: 0, bottom: 0, display: "flex", alignItems: "center", fontSize: 10, fontFamily: T.fontMono, color: T.text },
  message: { fontSize: 12, marginTop: 12 },
  latestCard: { background: "rgba(124,106,247,0.08)", border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 10 },
  latestHead: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  latestTag: { fontFamily: T.fontMono, fontSize: 9, letterSpacing: "1.5px", color: "#fff", background: T.acc, padding: "3px 8px", borderRadius: 6 },
  latestVersion: { fontSize: 15, fontWeight: 700, color: T.text },
  latestMeta: { fontSize: 11, color: T.muted, fontFamily: T.fontMono },
  urlRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  urlInput: { flex: 1, minWidth: 240, fontSize: 12, color: T.text, background: T.s1, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", fontFamily: T.fontMono },
  openBtn: { fontSize: 12, color: T.acc, textDecoration: "none", padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 8 },
  info: { fontSize: 12, color: T.muted, padding: "6px 4px" },
  tableWrap: { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12, overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontFamily: T.fontMono, fontSize: 9, letterSpacing: "1.5px", color: T.muted, padding: "6px 10px", borderBottom: `1px solid ${T.border}`, textTransform: "uppercase", whiteSpace: "nowrap" },
  tr: { borderBottom: `1px solid ${T.border}` },
  td: { padding: "8px 10px", fontSize: 12, color: T.text, verticalAlign: "middle", whiteSpace: "nowrap" },
  tdM: { padding: "8px 10px", fontSize: 11, color: T.muted, verticalAlign: "middle", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  link: { color: T.acc, textDecoration: "none", fontSize: 12 },
  empty: { padding: "16px 10px", fontSize: 12, color: T.muted, textAlign: "center" },
};
